import {MouseEvent, useState} from "react";
import {
  CreditCardOffOutlined,
  CreditCardOutlined,
  EventBusyOutlined,
  ListAltOutlined,
  MessageOutlined,
  NewReleasesOutlined,
} from "@mui/icons-material";
import {Box, Typography} from "@mui/material";
import {Notification} from "../../../types/Notification";
import NavigationBarNotificationItemStyle from "./NavigationBarNotificationItemStyle";
import truncate from "truncate-html";
import Grid from "@mui/material/Grid";
import {stripHtml} from "string-strip-html";
import dayjs from "dayjs";
import {apiPatch} from "../../../services/Api/apiCall";
import {useReleaseUpdateDialog} from "../../ReleaseUpdates/ReleaseUpdateDialogProvider";

const getIconForType = (type: string) => {
  switch (type) {
    case "release-update":
      return <NewReleasesOutlined />;
    case "new-message":
      return <MessageOutlined />;
    case "check-transfer":
      return <CreditCardOutlined />;
    case "deleted-transfer":
      return <CreditCardOffOutlined />;
    case "paid-task-request":
      return <ListAltOutlined />;
    case "paid-task-overdue":
      return <EventBusyOutlined />;
    default:
      return <NewReleasesOutlined />;
  }
};

export default function NavigationBarNotificationItem({
  notification,
  handleItemClick,
}: {
  notification: Notification;
  handleItemClick: () => void;
}) {
  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const { openReleaseUpdate } = useReleaseUpdateDialog();

  const markAsRead = async (notification: Notification) => {
    if (!notification.isRead) {
      await apiPatch("notifications", notification.id, { isRead: true });
    }
  };

  const getPlainText = (html: string): string => {
    return stripHtml(html).result;
  };

  const handleClick = async () => {
    if (notification.type === "release-update") {
      if (notification.isRead) {
        open(notification.link, "_blank");
      } else {
        await openReleaseUpdate(notification.releaseUpdatePublicationId);
      }
      handleItemClick();
      return;
    }

    if (notification.link) {
      open(notification.link, "_blank");
    }

    await markAsRead(notification);

    handleItemClick();
  };

  const handleInnerClick = async (e: MouseEvent) => {
    e.stopPropagation();

    if (notification.content && getPlainText(notification.content).length > 125) {
      if (!isContentExpanded && !notification.isRead) {
        markAsRead(notification);
      }
      setIsContentExpanded(!isContentExpanded);
    } else {
      handleClick();
    }
  };

  let displayedContent = notification.content;
  if (displayedContent && getPlainText(displayedContent).length > 125 && !isContentExpanded) {
    displayedContent = truncate(displayedContent, 125);
  }

  return (
    <Grid
      container
      spacing={0.5}
      onClick={handleClick}
      sx={{
        alignItems: "start",
        justifyContent: "space-between",
        width: "100%"
      }}>
      <Grid sx={{ paddingRight: "0.5rem" }}>
        {getIconForType(notification.type)}
      </Grid>
      <Grid
        sx={{
          flex: 1
        }}>
        <Typography variant="h6" sx={{ textWrap: "wrap", lineHeight: 1.2 }}>
          <strong>{notification.title}</strong>
        </Typography>
        <Typography variant={"subtitle2"} color="textSecondary" sx={{ mt: "0.25rem" }}>
          {dayjs(notification.created).format("DD.MM.YYYY HH:mm")} Uhr
        </Typography>
        {displayedContent && (
          <Box
            onClick={handleInnerClick}
            sx={NavigationBarNotificationItemStyle.contentBox}
            dangerouslySetInnerHTML={{ __html: displayedContent }}
          />
        )}
      </Grid>
      <Grid>
        <Box
          sx={{
            ...NavigationBarNotificationItemStyle.notificationBubble,
            visibility: notification.isRead ? "hidden" : "visible",
          }}
        >
          &#9679;
        </Box>
      </Grid>
    </Grid>
  );
}
